import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { 
  FileText, Loader2, Download, Users, 
  Calendar, CheckSquare, Square, Search,
  Printer
} from 'lucide-react';
import { toast } from 'sonner';
import { usePeriodo } from '../../contexts/PeriodoContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CartaoPontoManual = ({ token }) => {
  const { periodoSelecionado, alterarPeriodo } = usePeriodo();
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [empregados, setEmpregados] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);

  // Sincronizar com período global
  useEffect(() => {
    if (periodoSelecionado?.data_inicio && periodoSelecionado?.data_fim) {
      setDataInicio(periodoSelecionado.data_inicio);
      setDataFim(periodoSelecionado.data_fim);
    }
  }, [periodoSelecionado]);

  // Atualizar período global quando usuário altera manualmente
  const handleDataInicioChange = (novaData) => {
    setDataInicio(novaData);
    if (novaData && dataFim) {
      alterarPeriodo(novaData, dataFim);
    }
  };

  const handleDataFimChange = (novaData) => {
    setDataFim(novaData);
    if (dataInicio && novaData) {
      alterarPeriodo(dataInicio, novaData);
    }
  };

  // Buscar empregados
  useEffect(() => {
    const fetchEmpregados = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/cartao-ponto-manual/empregados`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setEmpregados(data.empregados || []);
        }
      } catch (err) {
        console.error('Erro ao buscar empregados:', err);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchEmpregados();
    }
  }, [token]);

  // Filtrar empregados
  const empregadosFiltrados = empregados.filter(emp => 
    emp.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.matricula?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.cargo?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Toggle seleção individual
  const toggleSelection = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) 
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };

  // Toggle selecionar todos
  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
    } else {
      setSelectedIds(empregadosFiltrados.map(e => e.id));
    }
    setSelectAll(!selectAll);
  };

  // Atualizar selectAll quando selectedIds mudar
  useEffect(() => {
    if (empregadosFiltrados.length > 0) {
      setSelectAll(selectedIds.length === empregadosFiltrados.length);
    }
  }, [selectedIds, empregadosFiltrados]);

  // Gerar cartões
  const gerarCartoes = async () => {
    if (selectedIds.length === 0) {
      toast.error('Selecione pelo menos um empregado');
      return;
    }

    if (!dataInicio || !dataFim) {
      toast.error('Defina o período');
      return;
    }

    setGenerating(true);
    
    try {
      const res = await fetch(`${API_URL}/api/cartao-ponto-manual/gerar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          empregado_ids: selectAll && selectedIds.length === empregados.length ? ['todos'] : selectedIds,
          data_inicio: dataInicio,
          data_fim: dataFim
        })
      });
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = selectedIds.length === 1 
          ? `cartao_ponto_${empregados.find(e => e.id === selectedIds[0])?.nome?.replace(/\s/g, '_') || 'empregado'}.pdf`
          : `cartoes_ponto_${dataInicio}_a_${dataFim}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        
        toast.success(`${selectedIds.length} cartão(ões) gerado(s) com sucesso!`);
        setDialogOpen(false);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erro ao gerar cartões');
      }
    } catch (err) {
      toast.error('Erro ao gerar cartões');
      console.error(err);
    } finally {
      setGenerating(false);
    }
  };

  // Formatar data para exibição
  const formatarData = (dataStr) => {
    if (!dataStr) return '';
    const [ano, mes, dia] = dataStr.split('-');
    return `${dia}/${mes}/${ano}`;
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Cartão de Ponto Manual
            </CardTitle>
            <CardDescription>
              Gere cartões de ponto em PDF para preenchimento manual
            </CardDescription>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="btn-gerar-cartao">
                <Printer className="w-4 h-4" />
                Gerar Cartões
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Gerar Cartões de Ponto
                </DialogTitle>
                <DialogDescription>
                  Selecione os empregados e o período para gerar os cartões
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
                {/* Período */}
                <div className="flex gap-4 items-end">
                  <div className="flex-1">
                    <Label>Data Início</Label>
                    <Input
                      type="date"
                      value={dataInicio}
                      onChange={(e) => handleDataInicioChange(e.target.value)}
                    />
                  </div>
                  <div className="flex-1">
                    <Label>Data Fim</Label>
                    <Input
                      type="date"
                      value={dataFim}
                      onChange={(e) => handleDataFimChange(e.target.value)}
                    />
                  </div>
                  <Badge variant="outline" className="h-10 px-3">
                    {formatarData(dataInicio)} a {formatarData(dataFim)}
                  </Badge>
                </div>
                
                {/* Busca */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Buscar por nome, matrícula ou cargo..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                
                {/* Seleção */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="select-all"
                      checked={selectAll}
                      onCheckedChange={toggleSelectAll}
                    />
                    <Label htmlFor="select-all" className="cursor-pointer">
                      Selecionar todos ({empregadosFiltrados.length})
                    </Label>
                  </div>
                  <Badge variant="secondary">
                    {selectedIds.length} selecionado(s)
                  </Badge>
                </div>
                
                {/* Lista de empregados */}
                <div className="flex-1 overflow-auto border rounded-lg">
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                    </div>
                  ) : empregadosFiltrados.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <Users className="w-10 h-10 mx-auto mb-2 opacity-30" />
                      <p>Nenhum empregado encontrado</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12"></TableHead>
                          <TableHead>Nome</TableHead>
                          <TableHead>Matrícula</TableHead>
                          <TableHead>Cargo</TableHead>
                          <TableHead>Setor</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {empregadosFiltrados.map((emp) => (
                          <TableRow 
                            key={emp.id}
                            className={`cursor-pointer ${selectedIds.includes(emp.id) ? 'bg-blue-50' : ''}`}
                            onClick={() => toggleSelection(emp.id)}
                          >
                            <TableCell>
                              <Checkbox
                                checked={selectedIds.includes(emp.id)}
                                onCheckedChange={() => toggleSelection(emp.id)}
                              />
                            </TableCell>
                            <TableCell className="font-medium">{emp.nome}</TableCell>
                            <TableCell>{emp.matricula || '-'}</TableCell>
                            <TableCell>{emp.cargo || '-'}</TableCell>
                            <TableCell>{emp.setor || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
                
                {/* Botões */}
                <div className="flex justify-end gap-2 pt-2">
                  <Button 
                    variant="outline" 
                    onClick={() => setDialogOpen(false)}
                  >
                    Cancelar
                  </Button>
                  <Button 
                    onClick={gerarCartoes}
                    disabled={generating || selectedIds.length === 0}
                    className="gap-2"
                    data-testid="btn-confirmar-gerar"
                  >
                    {generating ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                    Gerar {selectedIds.length > 0 ? `(${selectedIds.length})` : ''}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <div className="bg-slate-50 rounded-lg p-4">
          <div className="flex items-start gap-4">
            <div className="bg-blue-100 p-3 rounded-lg">
              <FileText className="w-8 h-8 text-blue-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-slate-800">Folha de Ponto Individual</h3>
              <p className="text-sm text-slate-600 mt-1">
                Gere cartões de ponto em branco para preenchimento manual. 
                Ideal para backup ou empresas em transição para o sistema digital.
              </p>
              <div className="flex gap-4 mt-3 text-xs text-slate-500">
                <span className="flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5" />
                  Período configurável
                </span>
                <span className="flex items-center gap-1">
                  <Users className="w-3.5 h-3.5" />
                  Individual ou em lote
                </span>
                <span className="flex items-center gap-1">
                  <Download className="w-3.5 h-3.5" />
                  PDF para impressão
                </span>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CartaoPontoManual;
